import { ContentItemFragmentFragment, OglinkFragmentFragment } from '@lopo-tech/lopo-api'
import * as React from 'react'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import Icon from 'src/components/forms/icon/icon'
import LinkPreview from 'src/components/link_preview/link_preview'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import Author from 'src/components/post/shared/author/author'
import ReactionOverview from 'src/components/post/shared/reactions/reaction_overview/reaction_overview'
import ShareButton from 'src/components/share/share_button/share_button'
import { CLASS_PREFIX } from 'src/constants/'
import FollowButton from 'src/containers/smart_components/follow_button/follow_button'
import {
  ITrackingProps,
  withTrackerProps
} from 'src/high_order_components/with_tracker_props'
import { POST_DISPLAY_MODE_PREVIEW, PURPOSE_BASIC } from 'src/interfaces/posts'
import { textResources } from 'src/lang/de'
import {
  POST_PREVIEW,
  PostPreviewObjType
} from 'src/utils/reporting/events_tracking/events_tracking'
import { UrlUtils } from 'src/utils/url/url'
import { EventLocationContainer } from '../shared/ingredients/event_location/event_location_container'
import Image from '../shared/ingredients/image/image'
import ImageSnapshot from '../shared/ingredients/image/image_snapshot'
import ImageWithDate from '../shared/ingredients/image_with_date/image_with_date'
import InclusionReason from '../shared/ingredients/inclusion_reason/inclusion_reason'
import { Teaser } from '../shared/ingredients/teaser/teaser'
import Title from '../shared/ingredients/title/title'
import { ReactionsCompact } from '../shared/reactions/reactions_button'
import './post_preview.scss'

const cls = CLASS_PREFIX + 'post__preview'
const labels = textResources.comments

export type IPostPreview = React.PropsWithChildren<
  {
    onVisibleChange?: (id: string, inView: boolean) => void;
    hideFooter?: boolean;
    inclusionReason?: string | null;
    linkPreviewParams?: OglinkFragmentFragment;
    withBorder?: boolean;
  } & ContentItemFragmentFragment
>;

const PostPreview: React.FC<
  IPostPreview & ITrackingProps<PostPreviewObjType>
> = ({
  canReact,
  canComment,
  commentsCount,
  publishedAt,
  author,
  hideFooter,
  id,
  inclusionReason,
  attachments,
  teaser,
  slug,
  title,
  linkPreviewParams,
  reactions,
  tracking,
  onVisibleChange,
  canSeeFullContent,
  children,
  oglink,
  canShare,
  withBorder,
  ...rest
}) => {
  const fenced = !canSeeFullContent.value
  const blurred = fenced ? 'full' : undefined

  if (oglink) {
    linkPreviewParams = oglink
  }

  // If there is no comment, we don't display 0 but just "Comment"
  const commentText = commentsCount
    ? labels.commentCount(commentsCount)
    : labels.comment

  const url = UrlUtils.detailPage(slug, rest.__typename)
  const purpose = PURPOSE_BASIC

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '0px 0px -64px 0px'
  })

  useEffect(() => {
    onVisibleChange?.(id, inView)
  }, [id, inView, onVisibleChange])

  return (
    <article className={`${cls}${!withBorder ? ' ' + cls + '--without-border' : ''}`} ref={ref}>
      <div className={cls + '__tile'}>
        <div>
          {rest.__typename === 'Snapshot'
            ? (
              <ImageSnapshot
                postDetailsURL={url}
                postId={id}
                displayMode={POST_DISPLAY_MODE_PREVIEW}
                data={attachments}
                purpose={purpose}
              />
            )
            : null}
          {rest.__typename === 'Event'
            ? (
              <ImageWithDate
                postDetailsURL={url}
                postId={id}
                displayMode={POST_DISPLAY_MODE_PREVIEW}
                data={{ dateString: rest.startTime, images: attachments }}
                purpose={purpose}
              />
            )
            : null}
          {rest.__typename === 'Article'
            ? (
              <Image
                postDetailsURL={url}
                postId={id}
                displayMode={POST_DISPLAY_MODE_PREVIEW}
                data={attachments}
                purpose={purpose}
              />
            )
            : null}
          <InclusionReason
            content={inclusionReason}
            displayMode={POST_DISPLAY_MODE_PREVIEW}
          />
          <Title
            displayMode={POST_DISPLAY_MODE_PREVIEW}
            postDetailsURL={url}
            purpose={purpose}
            title={title}
          />
          {rest.__typename === 'Event'
            ? (
              <EventLocationContainer
                data={rest.address}
                displayMode={POST_DISPLAY_MODE_PREVIEW}
                purpose={PURPOSE_BASIC}
                format={rest.format}
                // The address row here is a link to the detail page, where the
                // fence applies; the meeting link would lead straight out of the
                // app. Whether the server withholds it from a viewer who cannot
                // see the full content is not something this card can tell, so it
                // does not hand it out itself.
                onlineMeetingUrl={fenced ? undefined : rest.onlineMeetingUrl}
              />
            )
            : null}
          <Teaser postDetailsURL={url} teaser={teaser} blurred={blurred}/>
        </div>
        {linkPreviewParams &&
          <LinkPreview
            author={linkPreviewParams.author}
            description={linkPreviewParams.description || ''}
            displayUrl={linkPreviewParams.url}
            imageUrl={linkPreviewParams.image?.previewUrl}
            target={'_blank'}
            title={linkPreviewParams.title}
            url={linkPreviewParams.url}
          />
        }
        <div className={cls + '__author-more-section '}>
          <Author
            publishedAt={publishedAt}
            displayMode={POST_DISPLAY_MODE_PREVIEW}
            author={author}
          />
          <FollowButton actorProfile={author}/>
        </div>
        <ReactionOverview
          reactions={reactions}
          displayMode={POST_DISPLAY_MODE_PREVIEW}
          basePath={url}
          disabled={!canReact.value}
        />
        {hideFooter
          ? null
          : (
            <div className={cls + '__tile__footer'}>
              <ReactionsCompact
                reactableId={id}
                disabled={!canReact.value}
                reactions={reactions}
              />
              {canComment.value && (
                <NavigationItem
                  className={cls + '__comments-btn'}
                  target="_self"
                  url={`${url}#comments`}
                >
                  <Icon aria-hidden="true" name={'comment'} size={20}/>
                  <div className={cls + '__comments-text'}>{commentText}</div>
                </NavigationItem>
              )}
              {canShare.value && (
                <ShareButton
                  utmSource="nf"
                  url={url}
                  title={title}
                  tracking={tracking}
                />
              )}
            </div>
          )}
      </div>
      {children}
    </article>
  )
}

export default withTrackerProps<typeof POST_PREVIEW, IPostPreview>(
  POST_PREVIEW
)(PostPreview)
